import React, { useState, useEffect } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import locationService from "../services/locationService";

const LocationStartupNotification = ({
  onLocationGranted,
  onLocationDenied,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Check if we should show the location request dialog
    const checkAndShowLocationRequest = async () => {
      // Don't show if we've already shown it in this session
      if (hasShown) return;

      // Use the service method to determine if we should auto-request
      if (!locationService.shouldAutoRequestLocation()) return;

      // Show the dialog after a short delay
      setTimeout(() => {
        setShowDialog(true);
        setHasShown(true);
      }, 2000); // 2 second delay to let the app load
    };

    checkAndShowLocationRequest();
  }, [hasShown]);

  const handleRequestLocation = async () => {
    setIsRequesting(true);
    try {
      const location = await locationService.requestLocationPermission();
      setShowDialog(false);
      if (onLocationGranted) {
        onLocationGranted(location);
      }
    } catch (error) {
      console.log("Location request failed:", error.message);

      // Show more helpful error message
      if (error.code === error.PERMISSION_DENIED) {
        console.log(
          "Location permission was denied. User needs to enable it in browser settings."
        );
      } else if (error.code === error.POSITION_UNAVAILABLE) {
        console.log("Location is unavailable. Check device location settings.");
      } else if (error.code === error.TIMEOUT) {
        console.log("Location request timed out. Try again.");
      }

      if (onLocationDenied) {
        onLocationDenied(error);
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setShowDialog(false);
    if (onLocationDenied) {
      onLocationDenied(new Error("User dismissed location request"));
    }
  };

  return (
    <>
      {/* Dialog for location permission request */}
      <Dialog
        open={showDialog}
        onClose={handleDismiss}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LocationIcon color="primary" />
            <Typography variant="h6">Enable Location Services</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography variant="body1" paragraph>
              Arya can provide personalized weather information and
              location-based recommendations when you enable location services.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Your location data is stored locally on your device and is only
              used to provide relevant weather information. You can disable this
              at any time.
            </Typography>
            <Box mt={2}>
              <Alert severity="info">
                <AlertTitle>Benefits of enabling location:</AlertTitle>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Automatic weather updates for your current location</li>
                  <li>
                    Personalized recommendations based on local conditions
                  </li>
                  <li>Location-aware responses to your commands</li>
                  <li>Jarvis-like contextual assistance</li>
                </ul>
              </Alert>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDismiss} disabled={isRequesting}>
            Not Now
          </Button>
          <Button
            onClick={handleRequestLocation}
            variant="contained"
            startIcon={
              isRequesting ? <CircularProgress size={20} /> : <LocationIcon />
            }
            disabled={isRequesting}
          >
            {isRequesting ? "Requesting..." : "Enable Location"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LocationStartupNotification;
